import { useObjectContext } from '@components/v2/layout/ObjectScreen/hooks/useObjectContext';
import { useCallback } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import LangEnum from '../../../../../../lang/LangEnum';
import PATHS from '../../../../../../routes/routes.const';
import useOrderBookStore from '../../../../../../stores/OrderBook/orderBook.store';
import snackbarStore from '../../../../../../stores/Snackbar.store';
import { WithId } from '../../../../../../types/Base.types';
import { useApplicationContext } from '../../../../../context/ApplicationContext/ApplicationContext';
import { useLoadingStatusStore } from '../../../../../context/loadingStatus/LoadingStatus.store';
import { LoadingStatus } from '../../../../../context/loadingStatus/loadingStatus.store.types';
import { isOfflineStatus } from '../../../../../offline/domain/offlineOrder/offlineOrder.types';
import { useTranslation } from '../../../../../translation/TranslationContext';
import { ActionEnum } from '../../../../actions/types/orderActions.types';
import { OrderActionProps } from '../../../../actions/types/orderActionsContext.types';
import { useOrderContext } from '../../../../contexts/Order.context';
import { useOrderActionsContext } from '../../../../contexts/OrderActions.context';
import { useOrderCalculateContext } from '../../../../contexts/OrderCalculate.context';
import useOrderSummary from '../../../../contexts/hooks/useOrderSummary';
import { isActionToChangeStatus, needToGoBackToList } from '../../../../contexts/order.utils';
import { StatusType } from '../../../../order.types';
import { ORDER_OBJECT_TAB } from '../../../../orders.constants';
import { OrderView } from '../../../../ordersView.types';
import ContinueToConditionsButton from './ContinueToConditionsButton';
import FinishButton from './FinishButton';
import SuggestedOrderActionButton from './SuggestedOrderActionButton';

const FinishOrderButton = () => {
  const { team } = useApplicationContext();
  const history = useHistory();
  const location = useLocation();
  const { order, canEditOrder } = useOrderContext();
  const { activeTab, setActiveTab } = useObjectContext();
  const summary = useOrderSummary();
  const { calculateSummary } = useOrderCalculateContext();
  const { translate } = useTranslation();
  const [handleChangeBlockOrderBook, handleRemoveOrder, selectedOrder] = useOrderBookStore(
    useShallow((previousState) => [
      previousState.handleChangeBlockOrderBook,
      previousState.handleRemoveOrder,
      previousState.selectedOrder,
    ]),
  );
  const { onClickOrderAction } = useOrderActionsContext();

  const params = useParams<WithId>();
  const loadingStatus = useLoadingStatusStore((state) => state.loadingStatus);
  const { handleMessage } = snackbarStore;

  const handleFinishOrder = async () => {
    if (loadingStatus === LoadingStatus.LOADING) {
      return handleMessage({
        type: 'warning',
        message: translate(LangEnum.SNACKBAR_ORDER_CHANGES_NOT_SAVED),
      });
    }

    if (summary?.primaryAction) {
      handleChangeBlockOrderBook(true);

      if (order) {
        await onClickOrderAction(summary.primaryAction.value, OrderActionProps.orderToProps(order));
      }
      handleChangeBlockOrderBook(false);
      const stayInOrderPage = !needToGoBackToList({
        orderSplitSettings: order?.splitSettings,
        action: summary?.primaryAction?.value,
      });

      if (stayInOrderPage && isActionToChangeStatus(summary.primaryAction.value)) {
        calculateSummary();
      }
    }
  };

  const handleFinishEditSuggestedOrder = useCallback(async () => {
    if (selectedOrder?.id) {
      handleRemoveOrder(selectedOrder?.id, team?.id);
    }

    history.replace(
      `${PATHS.main.orders.path}/${params.id}/${OrderView.SUGGESTION}`,
      location.state,
    );
  }, [selectedOrder, handleRemoveOrder, team, history, params.id, location.state]);

  const handleAcceptSuggestion = async () => {
    if (order) {
      await onClickOrderAction(ActionEnum.ACCEPT_SUGGESTION, OrderActionProps.orderToProps(order));
    }
  };

  const onActionSuggestedOrder = canEditOrder
    ? handleFinishEditSuggestedOrder
    : handleAcceptSuggestion;

  if (order?.status === 'SUGGESTED') {
    return (
      <SuggestedOrderActionButton onClick={onActionSuggestedOrder} canEditOrder={canEditOrder} />
    );
  }

  const mustContinueToConditions =
    activeTab !== ORDER_OBJECT_TAB.CONDITIONS &&
    canEditOrder &&
    order?.status !== StatusType.ON_APPROVAL &&
    !isOfflineStatus(order?.status);

  if (mustContinueToConditions) {
    return <ContinueToConditionsButton onClick={() => setActiveTab(ORDER_OBJECT_TAB.CONDITIONS)} />;
  }

  return <FinishButton onClick={handleFinishOrder} />;
};

export default FinishOrderButton;
